import * as THREE from 'three';
import {
  GRAVITY, JUMP_FORCE, DOUBLE_JUMP_FORCE, MOVE_ACCEL, AIR_ACCEL,
  GROUND_FRICTION, AIR_DRAG, MAX_GROUND_SPEED, MAX_AIR_SPEED,
  SPRINT_MULTIPLIER, CROUCH_SPEED, SLIDE_INITIAL_BOOST, SLIDE_DURATION,
  SLIDE_COOLDOWN, WALL_SLIDE_SPEED, WALL_JUMP_FORCE, WALL_JUMP_UP,
  PLAYER_HEIGHT, CROUCH_HEIGHT, PLAYER_RADIUS, MAP_HALF,
  BASE_HEALTH, BASE_ARMOR, MAX_HEALTH, MAX_ARMOR,
} from '../shared/constants';
import { PlayerInput, Vector3 } from '../shared/types';
import { clamp, lerp } from '../shared/utils';

export interface AABB {
  minX: number; maxX: number;
  minY: number; maxY: number;
  minZ: number; maxZ: number;
}

export interface CollisionBox {
  position: Vector3;
  size: Vector3;
  color?: number;
  physics?: boolean;
}

export interface PhysicsWorld {
  gravity: number;
  friction: number;
  bounds: { minX: number; maxX: number; minZ: number; maxZ: number };
}

export interface PlayerPhysicsState {
  position: Vector3;
  velocity: Vector3;
  yaw: number;
  pitch: number;
  onGround: boolean;
  crouching: boolean;
  sprinting: boolean;
  sliding: boolean;
  wallSliding: boolean;
  grounded: boolean;
  lastGroundedTime: number;
  lastJumpTime: number;
  slideTimer: number;
  slideCooldown: number;
  jumpCount: number;
  health: number;
  armor: number;
  alive: boolean;
  lastDamageTime: number;
}

const defaultState = (): PlayerPhysicsState => ({
  position: { x: 0, y: 0, z: 0 },
  velocity: { x: 0, y: 0, z: 0 },
  yaw: 0,
  pitch: 0,
  onGround: true,
  crouching: false,
  sprinting: false,
  sliding: false,
  wallSliding: false,
  grounded: true,
  lastGroundedTime: 0,
  lastJumpTime: -999,
  slideTimer: 0,
  slideCooldown: 0,
  jumpCount: 0,
  health: BASE_HEALTH,
  armor: BASE_ARMOR,
  alive: true,
  lastDamageTime: 0,
});

export class PhysicsEngine {
  public world: PhysicsWorld;
  public obstacles: CollisionBox[] = [];

  constructor() {
    this.world = {
      gravity: GRAVITY,
      friction: GROUND_FRICTION,
      bounds: { minX: -MAP_HALF, maxX: MAP_HALF, minZ: -MAP_HALF, maxZ: MAP_HALF },
    };
  }

  createPlayerState(x = 0, y = 0, z = 0): PlayerPhysicsState {
    const state = defaultState();
    state.position = { x, y, z };
    return state;
  }

  simulatePlayer(state: PlayerPhysicsState, input: PlayerInput, dt: number, now: number): void {
    if (!state.alive) return;

    state.yaw = input.yaw;
    state.pitch = input.pitch;

    const height = state.crouching ? CROUCH_HEIGHT : PLAYER_HEIGHT;
    const eyeHeight = state.crouching ? 0.92 : 1.62;

    const forward = { x: -Math.sin(state.yaw), y: 0, z: -Math.cos(state.yaw) };
    const right = { x: Math.cos(state.yaw), y: 0, z: -Math.sin(state.yaw) };

    const wish = { x: 0, y: 0, z: 0 };
    if (input.forward) { wish.x += forward.x; wish.z += forward.z; }
    if (input.back) { wish.x -= forward.x; wish.z -= forward.z; }
    if (input.right) { wish.x += right.x; wish.z += right.z; }
    if (input.left) { wish.x -= right.x; wish.z -= right.z; }

    const wishLen = Math.sqrt(wish.x * wish.x + wish.z * wish.z);
    if (wishLen > 0) {
      wish.x /= wishLen;
      wish.z /= wishLen;
    }

    const isSliding = state.sliding && state.slideTimer > 0;

    let targetSpeed = state.onGround ? MAX_GROUND_SPEED : MAX_AIR_SPEED;
    if (state.sprinting && state.onGround && !state.crouching) {
      targetSpeed *= SPRINT_MULTIPLIER;
    }
    if (state.crouching && state.onGround && !isSliding) {
      targetSpeed = CROUCH_SPEED;
    }

    if (state.onGround) {
      const fric = Math.max(0, 1 - GROUND_FRICTION * dt);
      state.velocity.x *= fric;
      state.velocity.z *= fric;
    } else {
      const drag = Math.max(0, 1 - AIR_DRAG * dt);
      state.velocity.x *= drag;
      state.velocity.z *= drag;
    }

    const accel = state.onGround ? MOVE_ACCEL : AIR_ACCEL;
    state.velocity.x += wish.x * accel * dt;
    state.velocity.z += wish.z * accel * dt;

    const hSpeed = Math.sqrt(state.velocity.x ** 2 + state.velocity.z ** 2);
    const maxSpd = isSliding ? targetSpeed * SLIDE_INITIAL_BOOST : targetSpeed;
    if (hSpeed > maxSpd) {
      const scale = maxSpd / hSpeed;
      state.velocity.x *= scale;
      state.velocity.z *= scale;
    }

    if (isSliding) {
      state.slideTimer -= dt;
      if (state.slideTimer <= 0) {
        state.sliding = false;
      }
    }

    if (input.slide && state.onGround && !state.crouching &&
        now - state.lastGroundedTime <= 0.22 && state.slideCooldown <= 0) {
      state.sliding = true;
      state.slideTimer = SLIDE_DURATION;
      state.slideCooldown = SLIDE_COOLDOWN;
      state.velocity.x *= SLIDE_INITIAL_BOOST;
      state.velocity.z *= SLIDE_INITIAL_BOOST;
    }

    state.slideCooldown = Math.max(0, state.slideCooldown - dt);

    if (state.onGround && input.jump && state.lastJumpTime !== now) {
      state.velocity.y = JUMP_FORCE;
      state.onGround = false;
      state.lastJumpTime = now;
      state.jumpCount = 1;
      state.sliding = false;
    } else if (!state.onGround && input.jump && state.jumpCount < 2 &&
               now - state.lastJumpTime > 0.15) {
      state.velocity.y = DOUBLE_JUMP_FORCE;
      state.lastJumpTime = now;
      state.jumpCount++;
    }

    state.crouching = input.crouch;
    state.sprinting = input.sprint && state.onGround && !state.crouching;

    state.velocity.y -= GRAVITY * dt;

    this.moveWithCollision(state, dt);

    if (state.crouching) {
      state.position.y = Math.max(state.position.y, 0);
    }
  }

  private moveWithCollision(state: PlayerPhysicsState, dt: number): void {
    const prevY = state.position.y;
    state.position.y += state.velocity.y * dt;

    const half = PLAYER_RADIUS;
    const height = state.crouching ? CROUCH_HEIGHT : PLAYER_HEIGHT;

    if (state.position.y <= 0) {
      state.position.y = 0;
      if (state.velocity.y < 0) state.velocity.y = 0;
      state.onGround = true;
      state.grounded = true;
      state.lastGroundedTime = performance.now() / 1000;
    } else {
      state.onGround = false;
      state.grounded = false;
    }

    const prevX = state.position.x;
    state.position.x += state.velocity.x * dt;
    if (this.collidesAABB(state.position, half, height)) {
      state.position.x = prevX;
      state.velocity.x = 0;
    }

    const prevZ = state.position.z;
    state.position.z += state.velocity.z * dt;
    if (this.collidesAABB(state.position, half, height)) {
      state.position.z = prevZ;
      state.velocity.z = 0;
    }

    const b = this.world.bounds;
    state.position.x = clamp(state.position.x, b.minX + half, b.maxX - half);
    state.position.z = clamp(state.position.z, b.minZ + half, b.maxZ - half);
  }

  private collidesAABB(pos: Vector3, half: number, height: number): boolean {
    const minX = pos.x - half;
    const maxX = pos.x + half;
    const minZ = pos.z - half;
    const maxZ = pos.z + half;
    const minY = pos.y;
    const maxY = pos.y + height;

    for (const box of this.obstacles) {
      if (box.physics === false) continue;
      const bMinX = box.position.x - box.size.x / 2;
      const bMaxX = box.position.x + box.size.x / 2;
      const bMinZ = box.position.z - box.size.z / 2;
      const bMaxZ = box.position.z + box.size.z / 2;
      const bMaxY = box.position.y + box.size.y;
      const bMinY = box.position.y;

      if (maxY > bMinY && minY < bMaxY &&
          minX < bMaxX && maxX > bMinX &&
          minZ < bMaxZ && maxZ > bMinZ) {
        return true;
      }
    }
    return false;
  }

  takeDamage(state: PlayerPhysicsState, amount: number, now: number): boolean {
    if (!state.alive || amount <= 0) return false;
    state.lastDamageTime = now;
    let remaining = amount;
    if (state.armor > 0) {
      const absorbed = Math.min(state.armor, remaining * 0.6);
      state.armor -= absorbed;
      remaining -= absorbed;
    }
    state.health -= remaining;
    if (state.health <= 0) {
      state.health = 0;
      state.alive = false;
      return true;
    }
    return false;
  }

  respawn(state: PlayerPhysicsState, x: number, y: number, z: number): void {
    state.position = { x, y, z };
    state.velocity = { x: 0, y: 0, z: 0 };
    state.health = BASE_HEALTH;
    state.armor = BASE_ARMOR;
    state.alive = true;
    state.onGround = true;
    state.grounded = true;
    state.sliding = false;
    state.slideTimer = 0;
    state.jumpCount = 0;
  }

  getStateAABB(state: PlayerPhysicsState): AABB {
    const half = PLAYER_RADIUS;
    const height = state.crouching ? CROUCH_HEIGHT : PLAYER_HEIGHT;
    return {
      minX: state.position.x - half,
      maxX: state.position.x + half,
      minY: state.position.y,
      maxY: state.position.y + height,
      minZ: state.position.z - half,
      maxZ: state.position.z + half,
    };
  }

  lineIntersectsAABB(
    origin: Vector3,
    direction: Vector3,
    maxDistance: number,
    targets: { aabb: AABB; id: string }[]
  ): { id: string; distance: number; point: Vector3 } | null {
    let closest: { id: string; distance: number; point: Vector3 } | null = null;

    for (const target of targets) {
      const hit = this.rayAABBIntersect(origin, direction, target.aabb, maxDistance);
      if (hit && (!closest || hit.distance < closest.distance)) {
        closest = { id: target.id, distance: hit.distance, point: hit.point };
      }
    }

    return closest;
  }

  private rayAABBIntersect(
    origin: Vector3,
    dir: Vector3,
    box: AABB,
    maxDist: number
  ): { distance: number; point: Vector3 } | null {
    let tmin = -Infinity;
    let tmax = Infinity;
    const invDirX = dir.x === 0 ? Infinity : 1 / dir.x;
    const invDirY = dir.y === 0 ? Infinity : 1 / dir.y;
    const invDirZ = dir.z === 0 ? Infinity : 1 / dir.z;

    let t1 = (box.minX - origin.x) * invDirX;
    let t2 = (box.maxX - origin.x) * invDirX;
    if (t1 > t2) [t1, t2] = [t2, t1];
    tmin = Math.max(tmin, t1);
    tmax = Math.min(tmax, t2);
    if (tmin > tmax || tmax < 0) return null;

    t1 = (box.minY - origin.y) * invDirY;
    t2 = (box.maxY - origin.y) * invDirY;
    if (t1 > t2) [t1, t2] = [t2, t1];
    tmin = Math.max(tmin, t1);
    tmax = Math.min(tmax, t2);
    if (tmin > tmax || tmax < 0) return null;

    t1 = (box.minZ - origin.z) * invDirZ;
    t2 = (box.maxZ - origin.z) * invDirZ;
    if (t1 > t2) [t1, t2] = [t2, t1];
    tmin = Math.max(tmin, t1);
    tmax = Math.min(tmax, t2);
    if (tmin > tmax || tmax < 0) return null;

    const dist = tmin > 0 ? tmin : tmax;
    if (dist < 0 || dist > maxDist) return null;

    return {
      distance: dist,
      point: {
        x: origin.x + dir.x * dist,
        y: origin.y + dir.y * dist,
        z: origin.z + dir.z * dist,
      },
    };
  }
}
